import React, { useState } from 'react';
import { Plus, Search, Shield, Users, Eye, Edit2, Copy, Trash2, MoreVertical, ChevronRight, ChevronDown } from 'lucide-react';
import {
  useRoles,
  useRoleHierarchy,
  useDeleteRole,
  useCloneRole,
  countPermissions,
  type Role,
  type RoleHierarchyNode
} from '../../../services/queries/roleQueries';
import { DevModeBanner } from '../../common/DevModeBanner';
import { CreateRoleModal } from './CreateRoleModal';
import { EditRoleModal } from './EditRoleModal';
import { PermissionMatrixModal } from './PermissionMatrixModal';
import { useToast } from '../../../contexts/ToastContext';
import {
  StandardButton,
  StandardSearchBar,
  StandardLoadingState,
  StandardErrorState,
  StandardEmptyState
} from '../../standard';

export const RolesHome: React.FC = () => {
  // Toast notifications
  const { success, error: showError } = useToast();
  const [searchQuery, setSearchQuery] = useState('');
  const [viewMode, setViewMode] = useState<'list' | 'hierarchy'>('list');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingRole, setEditingRole] = useState<Role | null>(null);
  const [viewingPermissions, setViewingPermissions] = useState<Role | null>(null);
  const [roleMenuOpen, setRoleMenuOpen] = useState<string | null>(null);

  const { data: roles, isLoading, error } = useRoles({
    searchQuery,
    includeSystem: true
  });

  const { data: hierarchy } = useRoleHierarchy();
  const deleteRoleMutation = useDeleteRole();
  const cloneRoleMutation = useCloneRole();

  const totalRoles = roles?.length || 0;
  const customRoles = roles?.filter(r => !r.isSystem).length || 0;
  const totalUsers = roles?.reduce((sum, r) => sum + Number(r.userCount || 0), 0) || 0;

  const handleDelete = async (roleId: string, roleName: string) => {
    if (!window.confirm(`Are you sure you want to delete the role "${roleName}"?`)) return;

    try {
      await deleteRoleMutation.mutateAsync(roleId);
      success('Role deleted successfully!', `${roleName} has been permanently removed`);
      setRoleMenuOpen(null);
    } catch (error: any) {
      showError('Failed to delete role', error.message || 'Please try again');
    }
  };

  const handleClone = async (roleId: string) => {
    try {
      const cloned = await cloneRoleMutation.mutateAsync(roleId);
      setEditingRole(cloned);
      setRoleMenuOpen(null);
    } catch (error) {
      console.error('Failed to clone role:', error);
    }
  };

  return (
    <div className="p-6">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Roles & Permissions</h1>
            <p className="text-gray-600 dark:text-gray-300 mt-1">
              Manage roles and their permissions
            </p>
          </div>
          <StandardButton
            variant="primary"
            leftIcon={<Plus className="w-4 h-4" />}
            onClick={() => setShowCreateModal(true)}
          >
            New Role
          </StandardButton>
        </div>

        {/* Development Mode Banner */}
        <DevModeBanner
          dataSource="mockDataManager.ts"
          itemCount={roles ? `${roles.length} roles` : 'roles'}
          customMessage={`Development Mode: ${roles ? `Showing ${roles.length} roles` : 'Using mock data'} persisted via mockDataManager.ts (localStorage). Changes survive page refresh.`}
          dismissible={true}
        />

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-2xl font-bold text-gray-900 dark:text-white">
                  {totalRoles}
                </div>
                <div className="text-sm text-gray-600 dark:text-gray-300">Total Roles</div>
              </div>
              <div className="p-3 bg-purple-100 dark:bg-purple-900/20 rounded-lg">
                <Shield className="w-6 h-6 text-purple-600 dark:text-purple-400" />
              </div>
            </div>
          </div>

          <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-2xl font-bold text-gray-900 dark:text-white">
                  {customRoles}
                </div>
                <div className="text-sm text-gray-600 dark:text-gray-300">Custom Roles</div>
              </div>
              <div className="p-3 bg-blue-100 dark:bg-blue-900/20 rounded-lg">
                <Shield className="w-6 h-6 text-blue-600 dark:text-blue-400" />
              </div>
            </div>
          </div>

          <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-2xl font-bold text-gray-900 dark:text-white">
                  {totalUsers}
                </div>
                <div className="text-sm text-gray-600 dark:text-gray-300">Users Assigned</div>
              </div>
              <div className="p-3 bg-green-100 dark:bg-green-900/20 rounded-lg">
                <Users className="w-6 h-6 text-green-600 dark:text-green-400" />
              </div>
            </div>
          </div>
        </div>

        {/* Search and View Toggle */}
        <div className="flex flex-col md:flex-row gap-3">
          <div className="flex-1">
            <StandardSearchBar
              value={searchQuery}
              onChange={setSearchQuery}
              placeholder="Search roles by name or description..."
            />
          </div>

          <div className="flex items-center gap-2 bg-gray-100 dark:bg-gray-700 rounded-lg p-1">
            <button
              onClick={() => setViewMode('list')}
              className={`px-4 py-2 rounded-md transition-colors ${
                viewMode === 'list'
                  ? 'bg-white dark:bg-gray-800 text-purple-600 dark:text-purple-400 shadow-sm'
                  : 'text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white'
              }`}
            >
              List View
            </button>
            <button
              onClick={() => setViewMode('hierarchy')}
              className={`px-4 py-2 rounded-md transition-colors ${
                viewMode === 'hierarchy'
                  ? 'bg-white dark:bg-gray-800 text-purple-600 dark:text-purple-400 shadow-sm'
                  : 'text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white'
              }`}
            >
              Hierarchy View
            </button>
          </div>
        </div>
      </div>

      {/* Loading State */}
      {isLoading && (
        <StandardLoadingState message="Loading roles..." />
      )}

      {/* Error State */}
      {error && (
        <StandardErrorState
          title="Error loading roles"
          message="Please try again."
        />
      )}

      {/* Content */}
      {!isLoading && !error && (
        <>
          {viewMode === 'list' ? (
            // List View
            roles && roles.length > 0 ? (
              <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
                <div className="divide-y divide-gray-200 dark:divide-gray-700">
                  {roles.map((role) => (
                    <RoleRow
                      key={role.id}
                      role={role}
                      onViewPermissions={() => setViewingPermissions(role)}
                      onEdit={() => setEditingRole(role)}
                      onClone={() => handleClone(role.id)}
                      onDelete={() => handleDelete(role.id, role.name)}
                      menuOpen={roleMenuOpen === role.id}
                      onMenuToggle={() => setRoleMenuOpen(roleMenuOpen === role.id ? null : role.id)}
                    />
                  ))}
                </div>
              </div>
            ) : (
              <StandardEmptyState
                icon={<Shield className="w-16 h-16" />}
                title="No roles found"
                message={searchQuery
                  ? 'Try adjusting your search criteria'
                  : 'Get started by creating your first role'
                }
                actionLabel={searchQuery ? 'Clear search' : undefined}
                onAction={searchQuery ? () => setSearchQuery('') : undefined}
              />
            )
          ) : (
            // Hierarchy View
            <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Role Hierarchy</h3>
              {hierarchy && hierarchy.length > 0 ? (
                <div className="space-y-2">
                  {hierarchy.map((node) => (
                    <RoleHierarchyItem
                      key={node.id}
                      node={node}
                      level={0}
                      onViewPermissions={setViewingPermissions}
                      onEdit={setEditingRole}
                    />
                  ))}
                </div>
              ) : (
                <p className="text-gray-500 dark:text-gray-400 text-center py-8">
                  No role hierarchy available
                </p>
              )}
            </div>
          )}
        </>
      )}

      {/* Modals */}
      <CreateRoleModal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
      />

      <EditRoleModal
        role={editingRole}
        isOpen={!!editingRole}
        onClose={() => setEditingRole(null)}
      />

      <PermissionMatrixModal
        role={viewingPermissions}
        isOpen={!!viewingPermissions}
        onClose={() => setViewingPermissions(null)}
      />
    </div>
  );
};

// Role Row Component (List View)
interface RoleRowProps {
  role: Role;
  onViewPermissions: () => void;
  onEdit: () => void;
  onClone: () => void;
  onDelete: () => void;
  menuOpen: boolean;
  onMenuToggle: () => void;
}

const RoleRow: React.FC<RoleRowProps> = ({
  role,
  onViewPermissions,
  onEdit,
  onClone,
  onDelete,
  menuOpen,
  onMenuToggle
}) => {
  const permissions = countPermissions(role);

  return (
    <div className="p-4 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4 flex-1">
          <div className="w-12 h-12 rounded-lg bg-purple-100 dark:bg-purple-900/20 flex items-center justify-center">
            <Shield className="w-6 h-6 text-purple-600 dark:text-purple-400" />
          </div>

          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <span className="font-semibold text-gray-900 dark:text-white">{role.name}</span>
              {role.isSystem && (
                <span className="px-2 py-0.5 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 text-xs rounded font-medium">
                  System
                </span>
              )}
            </div>

            {role.description && (
              <p className="text-sm text-gray-600 dark:text-gray-300 mb-2">{role.description}</p>
            )}

            <div className="flex items-center gap-4 text-sm text-gray-500 dark:text-gray-400">
              <div className="flex items-center gap-1">
                <Users className="w-4 h-4" />
                <span>{Number(role.userCount) || 0} user{Number(role.userCount) !== 1 ? 's' : ''}</span>
              </div>
              <span>•</span>
              <div className="flex items-center gap-3">
                <span className="text-green-600 dark:text-green-400">{Number(permissions.read) || 0} Read</span>
                <span className="text-blue-600 dark:text-blue-400">{Number(permissions.write) || 0} Write</span>
                <span className="text-red-600 dark:text-red-400">{Number(permissions.delete) || 0} Delete</span>
              </div>
              {role.parentRoleName && (
                <>
                  <span>•</span>
                  <span>Reports to: {role.parentRoleName}</span>
                </>
              )}
            </div>
          </div>
        </div>

        <div className="flex items-center gap-2 ml-4">
          <button
            onClick={onViewPermissions}
            className="px-3 py-1.5 text-purple-600 dark:text-purple-400 hover:bg-purple-50 dark:hover:bg-purple-900/20 rounded-lg transition-colors flex items-center gap-2 text-sm font-medium"
          >
            <Eye className="w-4 h-4" />
            Permissions
          </button>

          <div className="relative">
            <button
              onClick={onMenuToggle}
              className="p-2 hover:bg-gray-100 dark:hover:bg-gray-600 rounded-lg transition-colors"
            >
              <MoreVertical className="w-5 h-5 text-gray-500 dark:text-gray-400" />
            </button>

            {menuOpen && (
              <>
                <div
                  className="fixed inset-0 z-40"
                  onClick={onMenuToggle}
                />
                <div className="absolute right-0 mt-2 w-48 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 z-50">
                  <div className="py-1">
                    <button
                      onClick={onEdit}
                      className="w-full px-4 py-2 text-left text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center gap-2"
                    >
                      <Edit2 className="w-4 h-4" />
                      Edit Role
                    </button>
                    <button
                      onClick={onClone}
                      className="w-full px-4 py-2 text-left text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center gap-2"
                    >
                      <Copy className="w-4 h-4" />
                      Clone Role
                    </button>
                    {!role.isSystem && (
                      <>
                        <div className="border-t border-gray-200 dark:border-gray-700 my-1" />
                        <button
                          onClick={onDelete}
                          disabled={role.userCount > 0}
                          className={`w-full px-4 py-2 text-left text-sm flex items-center gap-2 ${
                            role.userCount > 0
                              ? 'text-gray-400 dark:text-gray-600 cursor-not-allowed'
                              : 'text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20'
                          }`}
                          title={role.userCount > 0 ? 'Cannot delete role with assigned users' : ''}
                        >
                          <Trash2 className="w-4 h-4" />
                          Delete Role
                        </button>
                      </>
                    )}
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

// Role Hierarchy Node Component (Hierarchy View)
interface RoleHierarchyNodeProps {
  node: RoleHierarchyNode;
  level: number;
  onViewPermissions: (role: Role) => void;
  onEdit: (role: Role) => void;
}

const RoleHierarchyItem: React.FC<RoleHierarchyNodeProps> = ({
  node,
  level,
  onViewPermissions,
  onEdit
}) => {
  const [expanded, setExpanded] = useState(level < 2);
  const hasChildren = node.children.length > 0;

  return (
    <div>
      <div
        className={`flex items-center gap-2 p-3 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors ${
          level > 0 ? 'ml-8' : ''
        }`}
      >
        {hasChildren && (
          <button
            onClick={() => setExpanded(!expanded)}
            className="p-1 hover:bg-gray-200 dark:hover:bg-gray-600 rounded transition-colors"
          >
            {expanded ? (
              <ChevronDown className="w-4 h-4 text-gray-500" />
            ) : (
              <ChevronRight className="w-4 h-4 text-gray-500" />
            )}
          </button>
        )}
        {!hasChildren && <div className="w-6" />}

        <div className="w-8 h-8 rounded-lg bg-purple-100 dark:bg-purple-900/20 flex items-center justify-center flex-shrink-0">
          <Shield className="w-4 h-4 text-purple-600 dark:text-purple-400" />
        </div>

        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className="font-medium text-gray-900 dark:text-white">{node.name}</span>
            {node.isSystem && (
              <span className="px-2 py-0.5 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 text-xs rounded">
                System
              </span>
            )}
          </div>
          <div className="flex items-center gap-2 text-xs text-gray-500 dark:text-gray-400">
            <Users className="w-3 h-3" />
            <span>{Number(node.userCount) || 0} user{Number(node.userCount) !== 1 ? 's' : ''}</span>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <button
            onClick={() => onViewPermissions(node)}
            className="p-2 text-purple-600 dark:text-purple-400 hover:bg-purple-50 dark:hover:bg-purple-900/20 rounded-lg transition-colors"
            title="View Permissions"
          >
            <Eye className="w-4 h-4" />
          </button>
          <button
            onClick={() => onEdit(node)}
            className="p-2 text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
            title="Edit Role"
          >
            <Edit2 className="w-4 h-4" />
          </button>
        </div>
      </div>

      {expanded && hasChildren && (
        <div className="mt-1">
          {node.children.map((child: RoleHierarchyNode) => (
            <RoleHierarchyItem
              key={child.id}
              node={child}
              level={level + 1}
              onViewPermissions={onViewPermissions}
              onEdit={onEdit}
            />
          ))}
        </div>
      )}
    </div>
  );
};

